import {
  DataTableColumn,
  DataType,
  FormDialog,
  FormDialogMode,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useConfirmationDialog } from '../../../../../hooks'
import { NonAuthRoutes } from '../../../../../model'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { storeActiveBalanceZone, storeActiveCompany } from '../../../../../redux'
import {
  selectActiveBalanceZone,
  selectActiveCompany,
  selectBalanceZones,
  selectCompanies
} from '../../../../../redux/selectors/profile.selectors'
import { BalanceZoneStoreModel, CompanyStoreModel } from '../../../../../redux/store.model'

export interface configurationDialogProps {
  open: boolean
  onClose: () => void
}

interface ConfigurationModel {
  companyId: number
  balanceZoneId: number
}

export const ConfigurationDialog: React.FC<configurationDialogProps> = ({
  open = false,
  ...props
}) => {
  const { info } = useNotify()
  const [showDialog, setShowDialog] = React.useState<boolean>(open)

  const history = useHistory()
  const matchClientModule = useRouteMatch('/client')
  const matchContractIFrameModule = useRouteMatch('/contracts')
  const matchContractModule = useRouteMatch('/contract')
  const matchContactInfo = useRouteMatch('/contactInfo')
  const matchClientIframe = useRouteMatch('/clientsMod')
  const matchContractIframe = useRouteMatch('/clientsMod')
  const matchClientsIframe = useRouteMatch('/clients')

  const isClientModule =
    (matchClientModule && !matchClientModule.isExact) ||
    matchContractModule ||
    matchContractIFrameModule ||
    matchContactInfo ||
    matchClientIframe ||
    matchContactInfo ||
    matchClientsIframe ||
    matchContractIframe

  const companies = useSelector(selectCompanies)
  const balanceZones = useSelector(selectBalanceZones)
  const activeBalanceZone = useSelector(selectActiveBalanceZone)
  const activeCompany = useSelector(selectActiveCompany)

  const [configuration, setConfiguration] = React.useState<ConfigurationModel | null>(null)
  const [formInputs, setFormInputs] = React.useState<DataTableColumn[] | null>(null)

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  //#region Effects

  useEffect(() => {
    setShowDialog(open)
  }, [open])

  useEffect(() => {
    if (activeCompany !== undefined && activeBalanceZone !== undefined) {
      setConfiguration({ companyId: activeCompany.id, balanceZoneId: activeBalanceZone.id })
    }
  }, [activeCompany, activeBalanceZone])

  useEffect(() => {
    if (companies !== undefined && balanceZones !== undefined) {
      setFormInputs([
        {
          name: 'companyId',
          label: 'Empresa',
          type: DataType.SELECT,
          required: true,
          selectOptions: mapCompaniesToSelectOption(companies)
        },
        {
          name: 'balanceZoneId',
          label: 'Zona de balance',
          type: DataType.SELECT,
          required: true,
          selectOptions: mapBalanceZonesToSelectOption(balanceZones)
        }
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, balanceZones])

  //#endregion

  const saveConfiguration = (modifiedConfiguration: ConfigurationModel) => {
    if (activeBalanceZone?.id !== modifiedConfiguration.balanceZoneId) {
      storeActiveBalanceZone(+modifiedConfiguration.balanceZoneId)
    }
    if (activeCompany?.id !== modifiedConfiguration.companyId) {
      if (isClientModule) {
        getConfirmation(ConfirmationDialogs.CHANGE_COMPANY_LOSE_CHANGES).then(async () => {
          storeActiveCompany(+modifiedConfiguration.companyId)
          history.push(NonAuthRoutes.Client)
          info('La configuracion de empresa / zona de balance se ha modificado correctamente')
          closeConfirmation()
        })
      } else {
        storeActiveCompany(+modifiedConfiguration.companyId)
        info('La configuracion de empresa / zona de balance se ha modificado correctamente')
      }
    }

    props.onClose()
  }

  const mapCompaniesToSelectOption = (items: CompanyStoreModel[]): SelectOption[] => {
    return items && items.map(mapCompanyToSelectOption)
  }

  const mapCompanyToSelectOption = (item: CompanyStoreModel): SelectOption => {
    return (
      item && {
        value: item.id,
        label: item.description
      }
    )
  }

  const mapBalanceZonesToSelectOption = (items: BalanceZoneStoreModel[]): SelectOption[] => {
    return items && items.map(mapBalanceZoneToSelectOption)
  }

  const mapBalanceZoneToSelectOption = (item: BalanceZoneStoreModel): SelectOption => {
    return (
      item && {
        value: item.id,
        label: item.description
      }
    )
  }

  return (
    <>
      {formInputs && configuration && (
        <FormDialog
          title={'Configuracion'}
          onClose={formData => {
            if (formData !== undefined) {
              saveConfiguration(formData)
            }
            setShowDialog(false)
            props.onClose()
          }}
          inputs={formInputs}
          inputData={configuration}
          open={showDialog}
          mode={FormDialogMode.EDIT}
          okBtnLabel="Guardar"
          cancelBtnLabel="Cancelar"
        />
      )}
    </>
  )
}
